import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  InputAdornment,
  Alert,
  alpha,
} from '@mui/material';
import {
  AttachMoney,
  ContentCopy,
  Check,
  Send as SendIcon,
} from '@mui/icons-material';
import { GradientCard, GlassCard, AnimatedButton, CoinCard } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';
import Coins from '../../assets/coins/coins.json';
import WAV from 'multicoin-address-validator';
import toast from 'react-hot-toast';

// Replaced styled components with sx props

const coinGradients: { [key: string]: string } = {
  BTC: gradients.sunset,
  ETH: gradients.ocean,
  USDC: gradients.success,
  USDT: gradients.success,
  MATIC: gradients.lavender,
  SOL: gradients.primary,
  BNB: gradients.sunset,
};

export const PayPage: React.FC = () => {
  const [formData, setFormData] = useState({
    coin: 'ETH',
    address: '',
    amount: '',
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [validatedCoins, setValidatedCoins] = useState<string[]>([]);

  const coins = Coins as { [key: string]: any };

  // Generate crypto list from all coins in Coins.json
  const allCryptos = Object.keys(coins).map((coinKey) => {
    const coinData = coins[coinKey];
    return {
      value: coinKey,
      label: `${coinData.name || coinKey} (${coinKey})`,
      icon: coinData.symbol || coinKey.charAt(0),
    };
  }).sort((a, b) => {
    // Priority order: BTC, ETH, USDC, USDT first, then alphabetically
    const priority = ['BTC', 'ETH', 'USDC', 'USDT'];
    const aIndex = priority.indexOf(a.value);
    const bIndex = priority.indexOf(b.value);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex; // Both are priority, sort by priority order
    }
    if (aIndex !== -1) return -1; // a is priority, comes first
    if (bIndex !== -1) return 1;  // b is priority, comes first

    return a.label.localeCompare(b.label); // Both non-priority, sort alphabetically
  });

  // Validate address and detect compatible coins
  useEffect(() => {
    if (!formData.address || formData.address.trim().length === 0) {
      setValidatedCoins([]);
      setAddressError('');
      return;
    }

    const address = formData.address.trim();
    const validCoins: string[] = [];

    // Check all coins to see which ones this address is valid for
    Object.keys(coins).forEach((coinKey) => {
      const coinInfo = coins[coinKey];
      try {
        let isValid = false;
        if (coinInfo.ERC20) {
          // ERC20 tokens use Ethereum addresses
          isValid = WAV.validate(address, 'ETH');
        } else {
          isValid = WAV.validate(address, coinKey);
        }

        if (isValid) {
          validCoins.push(coinKey);
        }
      } catch (error) {
        // Validation error, skip this coin
      }
    });

    setValidatedCoins(validCoins);

    if (validCoins.length === 0) {
      setAddressError('Invalid address format. Please check and try again.');
    } else {
      setAddressError('');
      // Auto-select the first valid coin if not already selected
      if (!validCoins.includes(formData.coin)) {
        setFormData((prev) => ({ ...prev, coin: validCoins[0] }));
      }
    }
  }, [formData.address, coins, formData.coin]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setGeneratedLink('');
  };

  const handleGenerate = () => {
    if (!formData.address) {
      return;
    }
    const baseUrl = window.location.origin;
    const link = formData.amount
      ? `${baseUrl}/pay/${formData.coin}/${formData.address}?amount=${formData.amount}`
      : `${baseUrl}/pay/${formData.coin}/${formData.address}`;
    setGeneratedLink(link);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{
        background: gradients.mesh,
        py: (theme) => theme.spacing(8),
        borderRadius: '0 0 40px 40px',
        mb: (theme) => theme.spacing(6),
      }}>
        <Container maxWidth="lg">
          <Box textAlign="center" position="relative" zIndex={1}>
            <Typography
              variant="h2"
              fontWeight={800}
              sx={{
                color: '#fff',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              Create Payment Request
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: alpha('#fff', 0.9), maxWidth: 700, mx: 'auto' }}
            >
              Generate a custom crypto payment link without needing an account
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        {/* Steps */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <GlassCard
              sx={(theme) => ({
                p: 3,
                height: '100%',
                border: '2px solid transparent',
                transition: 'all 0.3s ease',
                ...( !formData.coin ? {
                  borderColor: theme.palette.primary.main,
                  boxShadow: '0px 12px 48px rgba(99, 102, 241, 0.2)',
                } : {}),
              })}
            >
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: gradients.primary,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 800,
                mb: 2,
              }}>1</Box>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ px: 2, pt: 1 }}>
                Choose Cryptocurrency
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
                Select which crypto you want to receive
              </Typography>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <GlassCard
              sx={(theme) => ({
                p: 3,
                height: '100%',
                border: '2px solid transparent',
                transition: 'all 0.3s ease',
                ...( formData.coin && !formData.address ? {
                  borderColor: theme.palette.primary.main,
                  boxShadow: '0px 12px 48px rgba(99, 102, 241, 0.2)',
                } : {}),
              })}
            >
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: gradients.primary,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 800,
                mb: 2,
              }}>2</Box>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ px: 2, pt: 1 }}>
                Enter Your Address
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
                Paste your wallet address where you'll receive payment
              </Typography>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <GlassCard
              sx={(theme) => ({
                p: 3,
                height: '100%',
                border: '2px solid transparent',
                transition: 'all 0.3s ease',
                ...( formData.address ? {
                  borderColor: theme.palette.primary.main,
                  boxShadow: '0px 12px 48px rgba(99, 102, 241, 0.2)',
                } : {}),
              })}
            >
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: gradients.primary,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 800,
                mb: 2,
              }}>3</Box>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ px: 2, pt: 1 }}>
                Set Amount & Share
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
                Optional: specify an amount and share your link
              </Typography>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Form */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <GlassCard sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Payment Details
              </Typography>

              <Stack spacing={3} sx={{ mt: 3 }}>
                {/* Cryptocurrency Select */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Cryptocurrency *
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={formData.coin}
                    onChange={(e) => handleChange('coin', e.target.value)}
                  >
                    {allCryptos.map((crypto) => (
                      <MenuItem key={crypto.value} value={crypto.value}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontSize: '1.5rem' }}>{crypto.icon}</Typography>
                          <Typography>{crypto.label}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Address Input */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Your Wallet Address *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={`Enter your ${formData.coin} address`}
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    multiline
                    rows={2}
                    error={!!addressError}
                    InputProps={{
                      sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                    }}
                  />
                  {addressError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {addressError}
                    </Alert>
                  )}
                  {validatedCoins.length > 0 && !addressError && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      ✓ Valid address! Compatible with {validatedCoins.length} token{validatedCoins.length > 1 ? 's' : ''} ({validatedCoins.slice(0, 5).join(', ')}{validatedCoins.length > 5 ? '...' : ''})
                    </Alert>
                  )}
                </Box>

                {/* Amount Input */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Amount (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => handleChange('amount', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography fontWeight={700} color="text.secondary">
                            {formData.coin}
                          </Typography>
                        </InputAdornment>
                      ),
                      sx: { fontSize: '1.25rem', fontWeight: 700 },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Leave empty to let the payer choose the amount
                  </Typography>
                </Box>

                {/* Generate Button */}
                <AnimatedButton
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={!formData.address}
                  onClick={handleGenerate}
                  sx={{
                    background: gradients.primary,
                    py: 1.5,
                    '&:hover': {
                      background: gradients.primary,
                      opacity: 0.9,
                    },
                  }}
                  startIcon={<SendIcon />}
                >
                  Generate Payment Link
                </AnimatedButton>
              </Stack>
            </GlassCard>
          </Grid>

          {/* Preview/Result */}
          <Grid item xs={12} md={6}>
            {!generatedLink ? (
              <GradientCard
                sx={{
                  p: 4,
                  background: gradients.mesh,
                  minHeight: 400,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <AttachMoney sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Payment Link Preview
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Fill in the form and generate your payment link. It will appear here.
                </Typography>
              </GradientCard>
            ) : (
              <GlassCard sx={{ p: 4 }}>
                <Stack spacing={3}>
                  <Box textAlign="center">
                    <Check
                      sx={{
                        fontSize: 64,
                        color: 'success.main',
                        background: alpha('#10b981', 0.1),
                        borderRadius: '50%',
                        p: 2,
                        mb: 2,
                      }}
                    />
                    <Typography variant="h5" fontWeight={700} gutterBottom color="success.main">
                      Payment Link Generated!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Share this link to receive {formData.coin} payments
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      Your Payment Link
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        background: alpha('#000', 0.05),
                        borderRadius: 2,
                        wordBreak: 'break-all',
                        fontSize: '0.875rem',
                      }}
                    >
                      {generatedLink}
                    </Box>
                  </Box>

                  <Stack spacing={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={copied ? <Check /> : <ContentCopy />}
                      onClick={handleCopy}
                      sx={{
                        background: copied ? gradients.success : gradients.primary,
                        '&:hover': {
                          background: copied ? gradients.success : gradients.primary,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {copied ? 'Copied!' : 'Copy Link'}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      href={generatedLink}
                      target="_blank"
                    >
                      Open Payment Page
                    </Button>
                  </Stack>

                  <Alert severity="info" sx={{ borderRadius: 3 }}>
                    <Typography variant="body2">
                      <strong>Tip:</strong> Bookmark this link or save it somewhere safe. You can share it with anyone to receive {formData.coin} payments.
                    </Typography>
                  </Alert>
                </Stack>
              </GlassCard>
            )}
          </Grid>
        </Grid>

        {/* Info Section */}
        <Box sx={{ mt: 6 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <GlassCard sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h2" sx={{ mb: 1 }}>🚀</Typography>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  No Account Needed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create payment links instantly without signing up
                </Typography>
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <GlassCard sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h2" sx={{ mb: 1 }}>🔒</Typography>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Secure & Private
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We never have access to your funds or private keys
                </Typography>
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <GlassCard sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h2" sx={{ mb: 1 }}>⚡</Typography>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Lightning Fast
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate and share payment links in seconds
                </Typography>
              </GlassCard>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};
